/**
 * Centralized permission helpers.
 *
 * RULE: every UI gate, route guard, and service mutation should ask one of
 * these helpers — never inline a role check. This keeps role behavior
 * tweakable in one place as new tournament types/roles arrive.
 */

import type {
  GameAssignment,
  Profile,
  Role,
  RoleAssignment,
  Tournament,
} from '../models';

/** The "spaces" of the app a user can land in. */
export type CommandCenterKind =
  | 'admin'
  | 'tournaments'
  | 'scorekeeper'
  | 'captain'
  | 'player'
  | 'locker_room'
  | 'stands'
  | 'front_desk'
  | 'communication_hub'
  | 'scheduler'
  | 'operations'
  | 'directory'
  | 'pro_shop'
  | 'concessions';

/** Default landing route per role — keeps role-aware UX consistent. */
export const ROLE_HOME: Record<Role, CommandCenterKind> = {
  admin: 'admin',
  scorekeeper: 'scorekeeper',
  referee: 'scorekeeper',
  captain: 'captain',
  player: 'player',
  fan: 'stands',
  staff: 'front_desk',
};

const ANY: ReadonlyArray<Role> = [
  'admin',
  'scorekeeper',
  'referee',
  'captain',
  'player',
  'fan',
  'staff',
];

/** Which roles may VIEW a given command center / space. */
const VIEW_RULES: Record<CommandCenterKind, ReadonlyArray<Role>> = {
  admin: ['admin', 'staff'],
  tournaments: ['admin', 'staff'],
  scorekeeper: ['scorekeeper', 'referee', 'admin'],
  captain: ['captain', 'admin'],
  player: ['player', 'captain', 'admin'],
  locker_room: ['captain', 'player', 'admin'],
  stands: ANY,
  front_desk: ANY,
  communication_hub: ['admin', 'staff'],
  scheduler: ['admin', 'staff'],
  operations: ['admin', 'staff'],
  directory: ['admin', 'staff', 'captain'],
  pro_shop: ANY,
  concessions: ANY,
};

export function canViewCommandCenter(role: Role, kind: CommandCenterKind): boolean {
  return VIEW_RULES[kind].includes(role);
}

/** Which spaces should appear in the navigation for this role. */
export function visibleCommandCenters(role: Role): CommandCenterKind[] {
  return (Object.keys(VIEW_RULES) as CommandCenterKind[]).filter((k) =>
    canViewCommandCenter(role, k),
  );
}

// --------------------------------------------------------------------------
// Action-level checks
// --------------------------------------------------------------------------

interface AssignmentLookup {
  /** Tournament-scoped role grants for this user. */
  roleAssignments?: RoleAssignment[];
  /** Per-game crew grants for this user. */
  gameAssignments?: GameAssignment[];
}

export function canManageTournament(
  user: Profile | null | undefined,
  tournament: Pick<Tournament, 'id'>,
  lookup: AssignmentLookup = {},
): boolean {
  if (!user) return false;
  if (user.globalRoles.includes('admin')) return true;
  return Boolean(
    lookup.roleAssignments?.some(
      (a) => a.profileId === user.id && a.tournamentId === tournament.id && a.role === 'admin',
    ),
  );
}

export function canScoreGame(
  user: Profile | null | undefined,
  gameId: string,
  lookup: AssignmentLookup = {},
): boolean {
  if (!user) return false;
  if (user.globalRoles.includes('admin')) return true;
  return Boolean(
    lookup.gameAssignments?.some(
      (a) =>
        a.profileId === user.id &&
        a.gameId === gameId &&
        (a.position === 'scorekeeper' || a.position === 'timekeeper') &&
        a.status !== 'declined',
    ),
  );
}

export function canEditRoster(
  user: Profile | null | undefined,
  ctx: { tournamentId: string; teamId: string },
  lookup: AssignmentLookup = {},
): boolean {
  if (!user) return false;
  if (user.globalRoles.includes('admin')) return true;
  return Boolean(
    lookup.roleAssignments?.some(
      (a) =>
        a.profileId === user.id &&
        a.tournamentId === ctx.tournamentId &&
        ((a.role === 'captain' && a.teamId === ctx.teamId) || a.role === 'admin'),
    ),
  );
}

export function canSendCommunication(
  user: Profile | null | undefined,
  tournamentId: string,
  lookup: AssignmentLookup = {},
): boolean {
  if (!user) return false;
  if (user.globalRoles.includes('admin')) return true;
  return Boolean(
    lookup.roleAssignments?.some(
      (a) =>
        a.profileId === user.id &&
        a.tournamentId === tournamentId &&
        (a.role === 'admin' || a.role === 'staff'),
    ),
  );
}

export function canViewLockerRoom(
  user: Profile | null | undefined,
  ctx: { tournamentId: string; teamId: string },
  lookup: AssignmentLookup = {},
): boolean {
  if (!user) return false;
  if (user.globalRoles.includes('admin')) return true;
  return Boolean(
    lookup.roleAssignments?.some(
      (a) =>
        a.profileId === user.id &&
        a.tournamentId === ctx.tournamentId &&
        a.teamId === ctx.teamId &&
        (a.role === 'captain' || a.role === 'player'),
    ),
  );
}
